import React from 'react';

import { ListingSearchResponse, SearchResultItem } from 'apis/handler/listing/listingSchema';
import { ReactComponent as SubwayIcon } from 'assets/icons/ic_subway.svg';
import { ReactComponent as TownIcon } from 'assets/icons/ic_town.svg';
import { ReactComponent as UnivIcon } from 'assets/icons/ic_univ.svg';
import { useWhereCenterStore } from 'features/home/store/useWhereCenterStore';
import type { WhereSearchItem } from 'features/home/store/useWhereSearchStore';
import { RecentlySearchResults } from 'features/home/ui/RecentlySearchResults';
import { useTranslation } from 'react-i18next';

import WhereItem from './WhereItem';

type WhereModalProps = {
  whereSearches: WhereSearchItem[];
  originalValue: string;
  setOriginalValue: (value: string) => void;
  setWhereValue: (value: string) => void;
  setWhereType: (value: string) => void;
  whereData?: ListingSearchResponse;
  onClose: () => void;
};

const WhereModal = ({
  whereSearches,
  whereData,
  originalValue,
  setOriginalValue,
  setWhereValue,
  setWhereType,
  onClose,
}: WhereModalProps) => {
  const { t } = useTranslation('search');
  const { setWhereCenter } = useWhereCenterStore();

  // 백엔드가 좌표를 주면 { lat, lng }, 아직 없으면(null) 지도는 이름 지오코딩으로 폴백한다.
  const toCenter = (item: SearchResultItem) =>
    item.latitude !== null && item.longitude !== null ? { lat: item.latitude, lng: item.longitude } : null;

  const handleClick = ({
    originalValue,
    value,
    type,
    center = null,
  }: {
    originalValue: string;
    value: string;
    type: string;
    center?: { lat: number; lng: number } | null;
  }) => {
    setOriginalValue(originalValue);
    setWhereValue(value);
    setWhereType(type);
    setWhereCenter({ name: originalValue, center });
    onClose();
  };

  const getHeaderText = () => {
    if (originalValue) {
      return 'Search results';
    }

    if (whereSearches.length > 0) {
      return 'Recently searched';
    }

    return 'Trending searches';
  };

  // originalValue 없고 whereSearches가 있으면 최근 검색 기록 보여주기
  if (!originalValue && whereSearches.length > 0) {
    return (
      <RecentlySearchResults whereSearches={whereSearches} handleClick={handleClick} headerText={getHeaderText()} />
    );
  }

  // originalValue 있거나 whereData가 있으면 검색 결과 보여주기
  if (
    !whereData ||
    (whereData.universities.length === 0 && whereData.towns.length === 0 && whereData.subwayStations.length === 0)
  ) {
    return (
      <div className="flex w-full flex-col gap-y-5 px-4 py-5 md:px-10 md:py-[30px]">
        <div className="text-c2 text-gray-50">{t('noMatchingData')}</div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-y-3 overflow-y-auto px-4 py-5 md:px-10 md:py-[30px]">
      <h3 className="text-[12px] text-[#A9A9A9] md:text-[14px]">{getHeaderText()}</h3>

      <div className="flex w-full flex-col">
        {whereData.universities.length > 0 && (
          <div className="flex w-full flex-col gap-y-1.25">
            <div className="w-full flex-col items-center gap-x-0.5 overflow-x-auto">
              {whereData.universities.map((item, index) => (
                <WhereItem
                  key={index}
                  icon={UnivIcon}
                  text={item.name}
                  searchTerm={originalValue}
                  onClick={() =>
                    handleClick({
                      originalValue: item.name,
                      value: item.name,
                      type: 'UNIVERSITY',
                      center: toCenter(item),
                    })
                  }
                />
              ))}
            </div>
          </div>
        )}

        {whereData.towns.length > 0 && (
          <div className="flex w-full flex-col gap-y-1.25">
            <div className="w-full flex-col items-center gap-x-0.5 overflow-x-auto">
              {whereData.towns.map((item, index) => (
                <WhereItem
                  key={index}
                  icon={TownIcon}
                  text={item.name}
                  searchTerm={originalValue}
                  onClick={() =>
                    handleClick({
                      originalValue: item.name,
                      value: item.code,
                      type: 'ADDRESS',
                      center: toCenter(item),
                    })
                  }
                />
              ))}
            </div>
          </div>
        )}

        {whereData.subwayStations.length > 0 && (
          <div className="flex w-full flex-col gap-y-1.25">
            <div className="w-full flex-col items-center gap-x-0.5 overflow-x-auto">
              {whereData.subwayStations.map((item, index) => (
                <WhereItem
                  key={index}
                  icon={SubwayIcon}
                  text={item.name}
                  searchTerm={originalValue}
                  onClick={() =>
                    handleClick({
                      originalValue: item.name,
                      value: item.name,
                      type: 'SUBWAY_STATION',
                      center: toCenter(item),
                    })
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhereModal;
